import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { getBrandPaymentMethods } from '../../shared/utils/cashTreasuryHelpers';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../core/contexts/AppDataContext';
import { useLocation } from 'react-router-dom';
import { 
  Plus,
  Search,
  Download,
  Upload,
  Calendar,
  DollarSign,
  CreditCard,
  FileText,
  RefreshCcw,
  ArrowUpDown,
  Link2,
  ArrowUp,
  FilePlus,
  Eye,
  X,
  Copy,
  CheckCircle,
  Filter,
  ChevronDown,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  LayoutGrid,
  Shield,
  Layers,
  Wallet,
  ClipboardList,
  TrendingUp,
  ChevronUp,
  Clock,
  XCircle,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  mapJournalOperationFromApi,
  mapJournalOperationToApi,
} from './treasuryApiMappers';
import {
  resolveJournalLabelKey,
  resolveJournalUnitKey,
} from './saudiTreasuryDefaults';
import {
  operationExportHeaders,
  mapOperationsForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  operationImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../shared/components/custom/CustomComponents.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
// import BonCommandeModal from '../facturation/invoices/BonCommandeModal'; // REMOVED - Modal deleted
import html2canvas from 'html2canvas';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
  QawaemErpViewModal,
} from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, nextCaisseEntityRef, sliceCaisseListItems, useCaisseEmployeeOptions, buildCaisseEmployeeSelectOptions, findCaisseEmployeeMeta, useCaisseOperationTypes, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { mergeStickySelectClass, QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';

// Ajouter la classe CSS pour l'animation de pulsation
const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0.7;
    }
  }
`;

// Standard designation list (Arabic — used in datalist)
const DESIGNATION_OPTIONS = [
  'تغذية الصندوق (بواسطة موظف)',
  'تغذية الصندوق الرئيسي',
  'إرجاع فائض للصندوق الرئيسي',
  'صرف راتب شهري',
  'سلفة على الراتب',
  'أجر خدمة منزلية',
  'أجر يومي أو أسبوعي',
  'مكافأة أو حافز',
  'مصاريف سفر (محلي)',
  'مصاريف سفر (بين المدن)',
  'نقل شخصي بدون مركبة متاحة',
  'مهمة فنية أو إدارية',
  'تذاكر طيران / حجز نقل',
  'استرداد مصاريف مهمة',
  'تغيير زيت',
  'شراء زيت محرك',
  'شراء فلتر زيت / ديزل / هواء',
  'إصلاح إطارات',
  'شراء أنبوب داخلي',
  'شراء بطارية مركبة',
  'غسيل مركبة',
  'شراء قطع ميكانيكية',
  'أجور يد عاملة ميكانيكية',
  'رسوم الفحص الفني',
  'وقود / بنزين',
  'شراء ورق A4',
  'حبر طابعة',
  'شراء خرطوشة',
  'شراء آلة تغليف',
  'شراء أظرف',
  'شراء بطاقة / طباعة بطاقة',
  'شراء معدات تقنية',
  'شراء إضاءة LED',
  'شراء محوّل LED',
  'شراء أنبوب نيون LED',
  'شراء لوحة LED',
  'قاطع / تفاضلي',
  'ترموستات / مرحّل / قاطع حراري',
  'كابلات ومجرات وبراغي',
  'أدوات كهربائية (كاوية لحام، كماشة…)',
  'شراء طوب (بالباليت)',
  'شراء أكياس إسمنت',
  'شراء رمل',
  'رسوم التفريغ (رافعة)',
  'شراء معجون / دهان / جبس',
  'شراء لاصق / مثبّت / شريط / مسامير',
  'شراء سلم / منصة',
  'شراء أدوات موقع (مثقاب، كماشة…)',
  'مستلزمات مكتب',
  'مستهلكات (قفازات، أقنعة…)',
  'مواد تنظيف',
  'أدوات صغيرة (مكانس، فرش…)',
  'شراء أثاث أو إصلاح (كرسي، مرحاض…)',
  'شراء قطع ثلاجة',
  'دفع كهربائي / سبّاك',
  'دفع مقاول فرعي',
  'إيجار شقة',
  'إيجار مركبة',
  'إرسال بريد مسجّل',
  'دفع ناقل (قطع، مواد…)',
  'استرداد من موظف',
  'استرداد مصروفات متنوعة',
  'استرداد سلفة موظف',
  'استرداد خطأ أو مصروف مزدوج',
  'تبرع لجمعية',
  'غرامة ورسوم قضائية',
  'رسوم بنكية / ضريبة تحويل',
  'عشاء / استقبال مهني',
  'هامش ربح موظف',
  'شراء مواد غذائية (فواكه/خضار)',
  'دفع فاتورة كهرباء',
  'دفع فاتورة مياه',
  'دفع فاتورة إنترنت',
  'دفع فاتورة هاتف',
  'معدات وقاية شخصية',
  'شراء طفايات حريق',
  'صيانة تكييف',
  'شراء مستلزمات صحية',
  'دفع تأمين مركبة',
  'دفع تأمين شامل',
  'دفع ضرائب ورسوم',
  'اشتراكات GOSI',
  'دفع GOSI',
  'سحب نقدي من البنك',
  'إيداع نقدي في البنك',
  'فوائد بنكية',
  'غرامات تأخير',
  'شراء برنامج',
  'شراء ترخيص برنامج',
  'استرداد للعميل',
  'رسوم جمرك وميناء',
  'رسوم شحن',
  'رسوم السجل التجاري',
  'إيجار معدات موقع',
  'دفع خدمة حراسة',
  'شراء وجبات خفيفة / مشروبات',
  'مصاريف تمثيل تجاري',
  'مصاريف إعلان',
  'شراء بطاقات عمل',
  'مصاريف تدريب',
  'مصاريف ندوة',
  'مصاريف تأشيرة',
  'تعبئة وقود من محطة',
  'دفع طابع جبائي',
  'رسوم كاتب عدل',
  'شراء أرض'
];

const extractUnitFromDesignation = (designation) => {
  if (!designation) return 'وحدة';
  const parMatch = designation.match(/\(par\s+([^)]+)\)/i);
  const arMatch = designation.match(/\(ب(?:ال|ـ)?([^)]+)\)/);
  const match = parMatch || arMatch;
  if (match && match[1]) {
    return match[1].trim().toLowerCase();
  }
  if (/carburant|gasoil|وقود|بنزين/i.test(designation)) return 'L';
  return 'وحدة';
};

const CaisseJournalPage = () => {
  const { t, i18n } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.caisse.pages.caisseJournalPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'العملية' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'العمليات' });
  const entityCodeLabel = p('entity_code_label', { defaultValue: 'الرمز' });
  const { employeeOptions } = useCaisseEmployeeOptions({ valueField: 'id' });
  const {
    operationCategories: liveOperationCategories,
    debitTypes: liveDebitTypes,
    creditTypes: liveCreditTypes,
  } = useCaisseOperationTypes();
  const buyerSelectOptions = useMemo(() => [
    { value: '', label: p('select_buyer') },
    ...employeeOptions.map((o) => ({
      value: o.meta?.name || o.label,
      label: o.label,
    })),
  ], [employeeOptions, p]);
  const responsibleSelectOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: p('select_responsible') }),
    [employeeOptions, p],
  );
  const witnessSelectOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: p('select_witness') }),
    [employeeOptions, p],
  );
  const translateJournalLabel = (val) => {
    if (!val) return val;
    const labelKey = resolveJournalLabelKey(val);
    return labelKey ? p(labelKey) : val;
  };
  const translateUnit = (val) => {
    if (!val) return val;
    const labelKey = resolveJournalUnitKey(val);
    return labelKey ? p(labelKey) : val;
  };
  const dateLocale = getBrandLocale(i18n.language);
  const paymentMethodOptions = getBrandPaymentMethods();
  const location = useLocation();
  const { 
    caisseData,
    transferJournalToPrincipale,
    getSoldes,
    addVehicleRegistration,
    deleteVehicleRegistration,
    getVehicleRegistrations,
    // Fonctions pour les bons de commande
    linkBonCommandeToOperation,
    getBonCommandeById,
    addBonCommande,
    getCustomers,
    getSuppliers
  } = useAppData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [filterType, setFilterType] = useState('all'); // all, credit, debit
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [journalFormTab, setJournalFormTab] = useState('voucher');
  const journalFormTabDefs = useMemo(() => ([
    { id: 'voucher', label: p('section_voucher'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'operation', label: p('section_operation_detail', { defaultValue: p('section_operation') }), icon: Layers, iconClassName: 'text-blue-400' },
    { id: 'amounts', label: p('section_amounts_notes'), icon: DollarSign, iconClassName: 'text-amber-400' },
  ]), [p]);
  const JOURNAL_FIELD_COUNT = 20;
  const journalFormTabs = useMemo(
    () => buildCaisseModalFormTabs(journalFormTabDefs, JOURNAL_FIELD_COUNT),
    [journalFormTabDefs],
  );
  const transferFormTabDefs = useMemo(() => ([
    { id: 'transfer', label: p('section_general'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'responsible', label: p('section_responsible'), icon: Shield, iconClassName: 'text-blue-400' },
  ]), [p]);
  const JOURNAL_TRANSFER_FIELD_COUNT = 5;
  const transferFormTabs = useMemo(
    () => buildCaisseModalFormTabs(transferFormTabDefs, JOURNAL_TRANSFER_FIELD_COUNT),
    [transferFormTabDefs],
  );
  const [transferFormTab, setTransferFormTab] = useState('transfer');
  const [transferType, setTransferType] = useState('principale'); // principale ou mensuelle
  const [affectationCodes, setAffectationCodes] = useState([]);
  const [operations, setOperations] = useState([]);
  const [defaultJournalCaisseId, setDefaultJournalCaisseId] = useState(3);
  const [editingOperationId, setEditingOperationId] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const loadOperations = React.useCallback(async () => {
    setLoadingOperations(true);
    try {
      const [rows, dashboard] = await Promise.all([
        localApiAdapter.get('/treasury/journal').catch(() => []),
        localApiAdapter.get('/tresorerie/position/by-type/caisse').catch(() => null),
      ]);
      const caisseAccounts = Array.isArray(dashboard?.accounts) ? dashboard.accounts : [];
      const journalCaisse = caisseAccounts.find((c) => c.type === 'secondary') || caisseAccounts[0];
      if (journalCaisse?.id) setDefaultJournalCaisseId(journalCaisse.id);
      const mapped = sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapJournalOperationFromApi));
      setOperations(mapped);
      const unitsSet = new Set(mapped.map((op) => op.unite).filter(Boolean));
      ['وحدة', 'kg', 'm', 'm2', 'm3', 'L', 'pcs', 'heure', 'jour', 'mois'].forEach((u) => unitsSet.add(u));
      setUnitOptions(Array.from(unitsSet));
      const bonLinks = {};
      mapped.forEach((op) => {
        if (op.bonCommandeId) {
          bonLinks[`PJ-${String(op.id).slice(-7)}`] = {
            id: op.bonCommandeId,
            invoiceNumber: op.bonCommande,
            type: op.bonCommandeType,
          };
        }
      });
      setLinkedBons(bonLinks);
    } catch (error) {
      console.error('Failed to load journal operations:', error);
      toast.error(p('toast_save_error'));
      setOperations([]);
    } finally {
      setLoadingOperations(false);
    }
  }, []);
  
  // New UI Design States
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [mainViewMode, setMainViewMode] = useState('all'); // 'all', 'par_type', 'par_statut'
  const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'credits', 'debits'
  
  // Date range filter
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Refs for dropdowns
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  // Liste des immatriculations de véhicules disponibles
  const [vehicleRegistrations, setVehicleRegistrations] = useState([]);
  
  // État pour le modal de bon de commande - MODAL REMOVED
  // const [showBonCommandeModal, setShowBonCommandeModal] = useState(false);
  // const [selectedPieceJustificative, setSelectedPieceJustificative] = useState(null);
  const [linkedBons, setLinkedBons] = useState({}); // Keep this - used to display existing linked bons
  
  // Nouveaux états pour la gestion des bons
  const [previewImage, setPreviewImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const fileInputRef = useRef(null);
  
  // Nouveaux états pour la génération de bons
  const [isGeneratingBon, setIsGeneratingBon] = useState(false);
  const bonTemplateRef = useRef(null);
  
  // NEW: options d'unités pour le champ "Unité"
  const [unitOptions, setUnitOptions] = useState([]);
  // ——— GESTION DES TYPES D'OPÉRATIONS ET DÉSIGNATIONS ———
// Fonctions pour synchroniser les opérations entre les pages
const syncDebitOperations = (operations) => {
  if (!operations || !Array.isArray(operations)) return;
  
  // Mettre à jour les catégories de débit avec les opérations correspondantes
  setOperationCategories(prevCategories => {
    const updatedDebit = prevCategories.debit.map(category => {
      const categoryCode = category.category.split(' - ')[0];
      const matchingOps = operations.filter(op => 
        op.codeTypeId === categoryCode || op.parent === categoryCode
      );
      
      if (matchingOps.length > 0) {
        return {
          ...category,
          operations: matchingOps.map(op => op.name || op.value || op.description)
        };
      }
      
      // Conserver les opérations par défaut pour CN - Caisse Numéro
      if (categoryCode === "CN" && (!category.operations || category.operations.length === 0)) {
        return {
          ...category,
          operations: ["Paiements salaires et avances", "Retour de fonds vers caisse principale"]
        };
      }
      
      return category;
    });
    
    return {
      ...prevCategories,
      debit: updatedDebit
    };
  });
};

const syncCreditOperations = (operations) => {
  if (!operations || !Array.isArray(operations)) return;
  
  // Mettre à jour les catégories de crédit avec les opérations correspondantes
  setOperationCategories(prevCategories => {
    const updatedCredit = prevCategories.credit.map(category => {
      const categoryCode = category.category.split(' - ')[0];
      const matchingOps = operations.filter(op => 
        op.codeTypeId === categoryCode || op.parent === categoryCode
      );
      
      if (matchingOps.length > 0) {
        return {
          ...category,
          operations: matchingOps.map(op => op.name || op.value || op.description)
        };
      }
      
      // Conserver les opérations par défaut pour CN - Caisse Numéro
      if (categoryCode === "CN" && (!category.operations || category.operations.length === 0)) {
        return {
          ...category,
          operations: ["Alimentations de caisse", "Alimentation caisse principale"]
        };
      }
      
      return category;
    });
    
    return {
      ...prevCategories,
      credit: updatedCredit
    };
  });
};

// Mappings des catégories vers leurs désignations spécifiques
const CATEGORY_TO_DESIGNATIONS = {
  "Paiements salaires et avances": [
    'Paiement salaire mensuel',
    'Avance sur salaire',
    'Paiement femme de ménage',
    'Paiement journalier ou hebdomadaire',
    "Paiement d'indemnité ou prime"
  ],
  "Retour de fonds vers caisse principale": [
    'Retour de fonds vers caisse principale',
    'Transfert de liquidités',
    'Dépôt en caisse principale'
  ],
  "Alimentations de caisse": [
    'Alimentation de caisse (par employé)',
    'Retrait espèces banque',
    'Entrée de liquidités'
  ],
  "Alimentation caisse principale": [
    'Alimentation de caisse principale',
    'Versement en caisse principale'
  ],
  "Achats bureautiques": [
    'Achat RAM papier',
    'Achat toner imprimante',
    'Achat cartouche',
    'Achat plastifieuse / plastification',
    'Achat enveloppes',
    'Achat badge / impression badge',
    'Achat matériel informatique'
  ],
  "Achats divers / fonctionnement": [
    'Achat fournitures bureau',
    'Achat consommables (gants, masques…)',
    'Achat produits nettoyage',
    'Achat petit matériel (balais, brosses…)',
    'Achat mobilier ou réparation'
  ],
  "Déplacements et missions": [
    'Frais de déplacement (local)',
    'Frais de déplacement (inter-régional)',
    'Transport personnel sans véhicule dispo',
    'Mission technique ou administrative',
    "Billet d'avion / réservation transport"
  ],
  "Achats véhicules et entretien": [
    'Vidange moteur',
    'Achat huile moteur',
    'Achat filtre à huile / gasoil / air',
    'Réparation pneu',
    'Achat chambre à air',
    'Achat batterie véhicule',
    'Lavage véhicule',
    'Achat pièces mécaniques',
    "Frais de main-d'œuvre mécanique"
  ],
  "Remboursements & régularisations": [
    'Remboursement personnel',
    'Remboursement dépenses diverses',
    'Remboursement par salarié (avance)',
    'Remboursement erreur ou double dépense'
  ]
};

// Structure pour stocker les catégories et opérations
  const [operationCategories, setOperationCategories] = useState({
    debit: [
      {
        category: "CN - Caisse Numéro",
        operations: ["Paiements salaires et avances", "Retour de fonds vers caisse principale"]
      },
      {
        category: "ADM - Administration",
        operations: ["Achats bureautiques"]
      },
      {
        category: "DIV - Divers",
        operations: ["Achats divers / fonctionnement"]
      },
      {
        category: "PR - Parc Roulant",
        operations: ["Déplacements et missions", "Achats véhicules et entretien"]
      }
    ],
    credit: [
      {
        category: "CN - Caisse Numéro",
        operations: ["Alimentations de caisse", "Alimentation caisse principale"]
      },
      {
        category: "DIV - Divers",
        operations: ["Remboursements & régularisations"]
      }
    ]
  });
  
  const [debitTypes, setDebitTypes] = useState([]);
  const [creditTypes, setCreditTypes] = useState([]);
  const [selectedOperationType, setSelectedOperationType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [customDesignations, setCustomDesignations] = useState([]);
  
  // ——— GESTION DES UNITÉS ———
  // Liste complète des unités les plus courantes (triées par fréquence d'utilisation)
  const STANDARD_UNITS = [
    // Unités de base les plus courantes
    'unité', 'pièce', 'pcs',
    // Masse
    'kg', 'g', 'mg', 'tonne', 't', 'quintal', 'q',
    // Volume liquide
    'L', 'litre', 'mL', 'millilitre', 'cL', 'centilitre', 'dL', 'décilitre', 'hL', 'hectolitre',
    // Distance/Longueur
    'm', 'mètre', 'cm', 'centimètre', 'mm', 'millimètre', 'km', 'kilomètre',
    // Surface
    'm²', 'm2', 'cm²', 'cm2', 'km²', 'km2', 'ha', 'hectare', 'are',
    // Volume (solide)
    'm³', 'm3', 'cm³', 'cm3', 'dm³', 'dm3',
    // Temps
    'heure', 'h', 'jour', 'j', 'semaine', 'mois', 'année', 'minute', 'min', 'seconde', 's',
    // Emballage
    'carton', 'palette', 'caisse', 'boîte', 'sachet', 'sac', 'paquet', 'lot',
    // Électricité/Énergie
    'kWh', 'Wh', 'kW', 'W', 'A', 'ampère', 'V', 'volt',
    // Autres
    'paire', 'douzaine', 'centaine', 'millier', 'rouleau', 'barre', 'plaque', 'feuille', 
    'tube', 'bidon', 'jerrycan', 'bouteille', 'flacon', 'pot', 'spray', 'aérosol'
  ];
  
  const allUnits = STANDARD_UNITS;
  
  useEffect(() => {
    if (liveOperationCategories?.debit?.length || liveOperationCategories?.credit?.length) {
      setOperationCategories(liveOperationCategories);
      setDebitTypes(liveDebitTypes);
      setCreditTypes(liveCreditTypes);
    }
  }, [liveOperationCategories, liveDebitTypes, liveCreditTypes]);

  // Charger les données depuis l'API
  useEffect(() => {
    void loadOperations().catch((err) => console.error('Failed to load journal operations:', err));
    setVehicleRegistrations(caisseData.vehicleRegistrations || []);
    
    try {
      const savedCategories = localStorage.getItem('operationCategories');
      if (savedCategories) {
        setOperationCategories(JSON.parse(savedCategories));
      }
      
      const debitOperations = localStorage.getItem('debitOperations');
      if (debitOperations) {
        try {
          syncDebitOperations(JSON.parse(debitOperations));
        } catch (e) {
          console.error('Failed to process debit operations:', e);
        }
      }
      
      const creditOperations = localStorage.getItem('creditOperations');
      if (creditOperations) {
        try {
          syncCreditOperations(JSON.parse(creditOperations));
        } catch (e) {
          console.error('Failed to process credit operations:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load operation categories:', error);
    }
  }, [loadOperations]);

  // Charger les codes d'affectation depuis localStorage
  useEffect(() => {
    try {
      const savedCodes = localStorage.getItem('affectationCodes');
      if (savedCodes) {
        setAffectationCodes(JSON.parse(savedCodes));
      } else {
        // Codes par défaut si rien n'est trouvé dans localStorage
        const defaultCodes = [
          { id: '1', codeTypeId: 'CN', value: 'CN-01/RYD', description: 'الصندوق الرئيسي — الرياض', responsable: 'محمد العتيبي' },
          { id: '2', codeTypeId: 'ADM', value: 'ADM RYD', description: 'الإدارة — الرياض', responsable: 'مكتب الرياض' },
          { id: '3', codeTypeId: 'DIV', value: 'DIVERS', description: p('aff_desc_misc_expenses'), responsable: 'محمد العتيبي' },
          { id: '4', codeTypeId: 'PR', value: 'PARC ROULANT', description: p('aff_desc_vehicle_mgmt'), responsable: 'خالد الشمري' },
          { id: '5', codeTypeId: 'I', value: 'I/16/23', description: p('aff_desc_site_16_2023'), responsable: 'فهد القحطاني' }
        ];
        setAffectationCodes(defaultCodes);
      }
    } catch (error) {
      console.error('Failed to load affectation codes:', error);
      toast.error(p('toast_affectation_load_error'));
    }
  }, []);

  // Handle outside clicks for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Vérifier si l'URL contient une action pour afficher directement le formulaire
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add') {
      openAddFormModal();
    }
  }, [location]);
  
  // Écouteur pour les mises à jour des opérations dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Vérifier si les opérations ont été mises à jour
      if (e.key === 'debitOperations' || e.key === 'creditOperations' || e.key === 'operationCategories') {
        console.log(`Storage update detected for: ${e.key}`);
        
        // Recharger les catégories
        try {
          if (e.key === 'operationCategories' && e.newValue) {
            setOperationCategories(JSON.parse(e.newValue));
          }
          
          // Recharger les opérations de débit
          if (e.key === 'debitOperations' && e.newValue) {
            const parsedDebitOps = JSON.parse(e.newValue);
            
            setOperationCategories(prevCategories => {
              const updatedDebit = prevCategories.debit.map(category => {
                const categoryCode = category.category.split(' - ')[0];
                const matchingOps = parsedDebitOps.filter(op => 
                  op.codeTypeId === categoryCode || op.parent === categoryCode
                );
                
                if (matchingOps.length > 0) {
                  return {
                    ...category,
                    operations: matchingOps.map(op => op.name || op.value || op.description)
                  };
                }
                return category;
              });
              
              return {
                ...prevCategories,
                debit: updatedDebit
              };
            });
          }
          
          // Recharger les opérations de crédit
          if (e.key === 'creditOperations' && e.newValue) {
            const parsedCreditOps = JSON.parse(e.newValue);
            
            setOperationCategories(prevCategories => {
              const updatedCredit = prevCategories.credit.map(category => {
                const categoryCode = category.category.split(' - ')[0];
                const matchingOps = parsedCreditOps.filter(op => 
                  op.codeTypeId === categoryCode || op.parent === categoryCode
                );
                
                if (matchingOps.length > 0) {
                  return {
                    ...category,
                    operations: matchingOps.map(op => op.name || op.value || op.description)
                  };
                }
                return category;
              });
              
              return {
                ...prevCategories,
                credit: updatedCredit
              };
            });
          }
        } catch (error) {
          console.error('Failed to update operations:', error);
        }
      }
    };
    
    // Ajouter l'écouteur d'événement au window
    window.addEventListener('storage', handleStorageChange);
    
    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Calcul des totaux
  const [totals, setTotals] = useState({
    credits: 0,
    debits: 0,
    solde: 0
  });

  // Calcul du solde pour chaque opération
  const [operationsWithSolde, setOperationsWithSolde] = useState([]);

  // Mettre à jour operationsWithSolde quand operations change
  useEffect(() => {
    let runningSolde = 0;
    const updatedOpsWithSolde = operations.map((op, index) => {
      if (index === 0) {
        runningSolde = op.credit - op.debit;
      } else {
        runningSolde += op.credit - op.debit;
      }
      return { ...op, solde: runningSolde };
    });
    
    setOperationsWithSolde(updatedOpsWithSolde);
    
    // Mettre à jour les totaux
    setTotals({
      credits: operations.reduce((acc, op) => acc + op.credit, 0),
      debits: operations.reduce((acc, op) => acc + op.debit, 0),
      solde: operations.reduce((acc, op) => acc + op.credit - op.debit, 0)
    });
  }, [operations]);

  // Statistiques alignées sur le pattern Facturiers (5 cartes KPI)
  const journalStats = {
    totalOperations: operations.length,
    avgPerOperation:
      operations.length > 0
        ? (totals.credits + totals.debits) / operations.length
        : 0
  };

  // Données pour formulaire d'ajout d'opération
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    operationType: '',
    designation: '',
    credit: '',
    debit: '',
    amount: '',
    // affectation field removed
    notes: '',
    acheteur: '',
    immatriculation: '',
    quantite: '1',
    unite: 'وحدة',
    prixUnitaire: '',
    typeBon: 'interne',
    bonInterne: {
      beneficiaireId: '',
      beneficiaire: '',
      temoin: '',
      description: ''
    },
    bonExterne: {
      fichier: null,
      fournisseurId: '',
      fournisseur: '',
      montantReel: '',
      description: ''
    },
    paymentMethod: 'cash',
    // Nouveaux champs pour les employés
    responsableId: '',
    temoinId: ''
  });

  // Données pour transfert
  const [transferData, setTransferData] = useState({
    date: new Date().toISOString().split('T')[0],
    montant: '',
    description: '',
    // Nouveaux champs pour les employés
    responsableId: '',
    temoinId: ''
  });

  const getDefaultTransferData = () => ({
    date: new Date().toISOString().split('T')[0],
    montant: '',
    description: '',
    responsableId: '',
    temoinId: '',
  });

  const getDefaultFormData = () => ({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    operationType: '',
    designation: '',
    credit: '',
    debit: '',
    amount: '',
    notes: '',
    acheteur: '',
    immatriculation: '',
    quantite: '1',
    unite: 'وحدة',
    prixUnitaire: '',
    typeBon: 'interne',
    bonInterne: {
      beneficiaireId: '',
      beneficiaire: '',
      temoin: '',
      description: '',
    },
    bonExterne: {
      fichier: null,
      fournisseurId: '',
      fournisseur: '',
      montantReel: '',
      description: '',
    },
    paymentMethod: 'cash',
    responsableId: '',
    temoinId: '',
  });

  const openAddFormModal = () => {
    setJournalFormTab('voucher');
    setEditingOperationId(null);
    setFormData({
      ...getDefaultFormData(),
      reference: nextCaisseEntityRef(
        settings,
        'journalOperation',
        'OPJ',
        operations,
        (op) => op.reference || op.id,
      ),
    });
    setShowAddForm(true);
  };

  const closeAddFormModal = () => {
    setJournalFormTab('voucher');
    setEditingOperationId(null);
    setFormData(getDefaultFormData());
    setShowAddForm(false);
  };

  const closeTransferModal = () => {
    setTransferFormTab('transfer');
    setTransferData(getDefaultTransferData());
    setSelectedOperations([]);
    setShowTransferModal(false);
  };

  // Données pour nouvelle immatriculation
  const [newRegistration, setNewRegistration] = useState({
    immatriculation: '',
    description: ''
  });

  const [selectedOperations, setSelectedOperations] = useState([]);

  const unitSelectOptions = React.useMemo(() => {
    const merged = [...new Set([...allUnits, ...unitOptions, formData.unite].filter(Boolean))];
    return merged.map((unit) => ({
      value: unit,
      label: translateUnit(unit),
    }));
  }, [unitOptions, formData.unite, i18n.language]);

  
  // Charger les vraies données depuis le contexte
  const clients = getCustomers();
  const fournisseurs = getSuppliers();
  
  
  // Gérer les changements dans le formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'operationType') {
      setSelectedOperationType(value);
      
      // Trouver la catégorie sélectionnée
      let selectedCategoryData = null;
      let isDebit = false;
      let isCredit = false;
      
      // Chercher dans les catégories de débit
      for (const category of operationCategories.debit) {
        if (category.category === value) {
          selectedCategoryData = category;
          isDebit = true;
          break;
        }
      }
      
      // Si non trouvé, chercher dans les catégories de crédit
      if (!selectedCategoryData) {
        for (const category of operationCategories.credit) {
          if (category.category === value) {
            selectedCategoryData = category;
            isCredit = true;
            break;
          }
        }
      }
      
      // Mettre à jour les options de catégorie disponibles
      if (selectedCategoryData) {
        // Ensure we have default operations for CN - Caisse Numéro
        if (selectedCategoryData.category === "CN - Caisse Numéro" && (!selectedCategoryData.operations || selectedCategoryData.operations.length === 0)) {
          const defaultOps = isDebit 
            ? ["Paiements salaires et avances", "Retour de fonds vers caisse principale"]
            : ["Alimentations de caisse", "Alimentation caisse principale"];
          setCategoryOptions(defaultOps);
        } else {
          setCategoryOptions(selectedCategoryData.operations || []);
        }
        setSelectedCategory('');
      } else {
        setCategoryOptions([]);
      }
      
      // Si c'est un type de débit, on met le montant dans débit
      if (isDebit) {
        setFormData({
          ...formData,
          operationType: value,
          designation: value,
          credit: '',
          debit: formData.amount || '',
        });
      } 
      // Si c'est un type de crédit, on met le montant dans crédit
      else if (isCredit) {
        setFormData({
          ...formData,
          operationType: value,
          designation: value,
          credit: formData.amount || '',
          debit: '',
        });
      }
      return;
    }
    
    if (name === 'categorySelect') {
      setSelectedCategory(value);
      
      // Update designation options based on the selected category
      if (value && CATEGORY_TO_DESIGNATIONS[value]) {
        setDesignationOptions(CATEGORY_TO_DESIGNATIONS[value]);
      } else {
        setDesignationOptions([]);
      }
      
      setFormData({
        ...formData,
        designation: value
      });
      return;
    }
    
    // Removed handling for affectation field
    
    // Gérer le téléchargement de fichier pour bon externe
    if (e.target.type === 'file') {
      const file = e.target.files[0];
      
      if (file) {
        // Vérifier le type de fichier
        const fileType = file.type.toLowerCase();
        if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png')) {
          toast.error(p('toast_file_format_error'));
          e.target.value = ''; // Reset input
          return;
        }
        
        // Créer une URL pour l'aperçu de l'image
        const imageUrl = URL.createObjectURL(file);
        
        setFormData({
          ...formData,
          bonExterne: {
            ...formData.bonExterne,
            fichier: file,
            previewUrl: imageUrl
          }
        });
      }
      return;
    }
    
    if (name === 'responsableId' && value) {
      const selectedEmployee = findCaisseEmployeeMeta(employeeOptions, value);
      if (selectedEmployee) {
        setFormData({
          ...formData,
          [name]: value,
          acheteur: selectedEmployee.name || '',
        });
        return;
      }
    }
    
    // Les anciennes conditions credit/debit sont supprimées car nous utilisons maintenant un seul champ montant
    
    // Désignation is now just a free text field without auto-completion
    if (name === 'designation') {
      setFormData({
        ...formData,
        designation: value
      });
      return;
    }
    
    if (name.startsWith('bonInterne.') || name.startsWith('bonExterne.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Fonction pour ouvrir le sélecteur de fichier
  const handleUploadClick = (operationId) => {
    if (!fileInputRef.current) return;
    
    // Stocker l'ID de l'opération courante
    fileInputRef.current.dataset.operationId = operationId;
    fileInputRef.current.click();
  };
  
  // Gérer le téléchargement d'un document pour un bon existant
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    const fileType = file.type.toLowerCase();
    if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png')) {
      toast.error(p('toast_file_format_error'));
      e.target.value = ''; // Reset input
      return;
    }
    
    // Récupérer l'ID de l'opération depuis l'attribut data
    const operationId = fileInputRef.current.dataset.operationId;
    if (!operationId) return;
    
    // Trouver l'opération correspondante
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    
    // Créer une URL pour l'aperçu de l'image
    const imageUrl = URL.createObjectURL(file);
    
    // Simuler l'upload en créant un chemin fictif
    const mockFilePath = `bons/operation_${operationId}_${file.name}`;
    
    // Créer et lier un bon externe
    try {
      const bonData = {
        clientName: operation.acheteur || p('supplier_label'),
        clientId: 'F001', // ID fictif
        clientCode: 'FRN-1001', // Code fictif
        total: operation.debit > 0 ? operation.debit : operation.credit,
        date: operation.date,
        description: operation.designation,
        type: 'external',
        status: 'pending',
        scannedDocument: mockFilePath,
        previewUrl: imageUrl,
        items: []
      };

      const createdBon = addBonCommande(bonData);
      const success = linkBonCommandeToOperation(operation.id, createdBon.id);
      
      if (success) {
        const pjKey = `PJ-${String(operation.id).slice(-7)}`;
        setLinkedBons(prev => ({
          ...prev,
          [pjKey]: createdBon
        }));
        toast.success(p('toast_document_uploaded'));
      }
    } catch (error) {
      toast.error(p('toast_external_bon_error'));
    }
    
    // Reset input
    e.target.value = '';
  };
  
  // Afficher l'aperçu d'une image
  const handlePreviewImage = (bon) => {
    if (!bon) return;
    
    // Si c'est un bon avec une image générée ou URL d'aperçu (nouveau cas)
    if (bon.bonImage) {
      setPreviewImage(bon.bonImage);
      setShowImagePreview(true);
      
      // Ajouter un écouteur d'événement pour la touche Échap
      document.addEventListener('keydown', handleEscapeKey);
      return;
    }
    
    // Si c'est un bon externe avec une URL d'aperçu
    if (bon.type === 'external' && bon.previewUrl) {
      setPreviewImage(bon.previewUrl);
      setShowImagePreview(true);
      
      // Ajouter un écouteur d'événement pour la touche Échap
      document.addEventListener('keydown', handleEscapeKey);
      return;
    }
    
    // Si c'est un bon externe sans URL d'aperçu (cas existant)
    if (bon.type === 'external' && bon.scannedDocument) {
      // Simuler un aperçu avec une image fictive
      setPreviewImage(`https://via.placeholder.com/800x600.png?text=Simulation+Bon+${bon.invoiceNumber}`);
      setShowImagePreview(true);
      
      // Ajouter un écouteur d'événement pour la touche Échap
      document.addEventListener('keydown', handleEscapeKey);
      return;
    }
    
    // Si c'est un bon interne sans image générée (ancien cas)
    if (bon.type === 'internal') {
      // Générer une image pour le bon interne
      setPreviewImage(`https://via.placeholder.com/800x600.png?text=Bon+Interne+${bon.invoiceNumber}`);
      setShowImagePreview(true);
      
      // Ajouter un écouteur d'événement pour la touche Échap
      document.addEventListener('keydown', handleEscapeKey);
      return;
    }
    
    // Par défaut, afficher un message d'erreur
    toast.error(p('toast_bon_display_error'));
  };
  
  // Gestionnaire pour la touche Échap
  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      setShowImagePreview(false);
      setPreviewImage(null);
      // Supprimer l'écouteur d'événement
      document.removeEventListener('keydown', handleEscapeKey);
    }
  };
  
  // Nettoyer l'écouteur d'événement lors de la fermeture de la modal
  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);
  
  // Gérer les clics en dehors du dropdown de désignation
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDesignationDropdown && 
          event.target.name !== 'designation' && 
          !event.target.closest('.designation-dropdown')) {
        setShowDesignationDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDesignationDropdown]);
  
  // Écouteur d'événement pour les mises à jour des opérations et codes d'affectation en temps réel
  useEffect(() => {
    // Fonction pour surveiller les changements de localStorage
    const handleStorageChange = (event) => {
      // Si les opérations de débit ou crédit sont modifiées dans un autre onglet
      if (event.key === 'debitOperations' || event.key === 'creditOperations' || 
          event.key === 'operationCategories' || event.key === 'affectationCodes') {
        console.log(`localStorage update detected for: ${event.key}`);
        
        // Recharger les données selon ce qui a été modifié
        try {
          if (event.key === 'operationCategories' && event.newValue) {
            setOperationCategories(JSON.parse(event.newValue));
          }
          
          if (event.key === 'debitOperations' && event.newValue) {
            syncDebitOperations(JSON.parse(event.newValue));
          }
          
          if (event.key === 'creditOperations' && event.newValue) {
            syncCreditOperations(JSON.parse(event.newValue));
          }
          
          if (event.key === 'affectationCodes' && event.newValue) {
            setAffectationCodes(JSON.parse(event.newValue));
          }
        } catch (error) {
          console.error('Failed to sync data:', error);
        }
      }
    };
    
    // Ajouter l'écouteur d'événement storage
    window.addEventListener('storage', handleStorageChange);
    
    // Fonction de nettoyage
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Mettre à jour dynamiquement les mappings des catégories aux désignations
  const [dynamicDesignationMappings, setDynamicDesignationMappings] = useState({});
  
  // Charger ou mettre à jour les mappings depuis localStorage
  useEffect(() => {
    const savedMappings = localStorage.getItem('categoryToDesignations');
    if (savedMappings) {
      try {
        setDynamicDesignationMappings(JSON.parse(savedMappings));
      } catch (error) {
        console.error('Failed to load designation mappings:', error);
      }
    }
  }, []);
  
  // Écouter les changements des mappings dans le localStorage
  useEffect(() => {
    const handleStorageMappings = (event) => {
      if (event.key === 'categoryToDesignations' && event.newValue) {
        try {
          setDynamicDesignationMappings(JSON.parse(event.newValue));
        } catch (error) {
          console.error('Failed to update designation mappings:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageMappings);
    
    return () => {
      window.removeEventListener('storage', handleStorageMappings);
    };
  }, []);
  
  // Mettre à jour les options de catégorie quand operationCategories change
  useEffect(() => {
    // Si un type d'opération est sélectionné, mettre à jour les catégories disponibles
    if (selectedOperationType) {
      // Chercher la catégorie sélectionnée
      let selectedCategoryData = null;
      
      // Chercher dans les catégories de débit
      for (const category of operationCategories.debit) {
        if (category.category === selectedOperationType) {
          selectedCategoryData = category;
          break;
        }
      }
      
      // Si non trouvé, chercher dans les catégories de crédit
      if (!selectedCategoryData) {
        for (const category of operationCategories.credit) {
          if (category.category === selectedOperationType) {
            selectedCategoryData = category;
            break;
          }
        }
      }
      
      // Mettre à jour les options de catégorie
      if (selectedCategoryData) {
        setCategoryOptions(selectedCategoryData.operations || []);
        
        // Si la catégorie sélectionnée n'existe plus dans les nouvelles options, la réinitialiser
        if (selectedCategory && selectedCategoryData.operations && 
            !selectedCategoryData.operations.includes(selectedCategory)) {
          setSelectedCategory('');
          setDesignationOptions([]);
        } 
        // Sinon, mettre à jour les options de désignation en cherchant d'abord dans les mappings dynamiques
        else if (selectedCategory) {
          if (dynamicDesignationMappings[selectedCategory]) {
            setDesignationOptions(dynamicDesignationMappings[selectedCategory]);
          } else if (CATEGORY_TO_DESIGNATIONS[selectedCategory]) {
            setDesignationOptions(CATEGORY_TO_DESIGNATIONS[selectedCategory]);
          } else {
            setDesignationOptions([]);
          }
        }
      }
    }
  }, [operationCategories, selectedOperationType, selectedCategory, dynamicDesignationMappings]);
  
  // Nettoyer l'écouteur d'événement lorsque la modal se ferme
  useEffect(() => {
    if (!showImagePreview) {
      document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [showImagePreview]);

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'responsableId' && value) {
      setTransferData({
        ...transferData,
        [name]: value,
      });
      return;
    }
    
    setTransferData({
      ...transferData,
      [name]: value
    });
  };

  const toggleOperationSelection = (operationId) => {
    setSelectedOperations(prev => {
      if (prev.includes(operationId)) {
        return prev.filter(id => id !== operationId);
      } else {
        return [...prev, operationId];
      }
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.credit && !formData.debit) {
      toast.error(p('toast_amount_required'));
      return;
    }

    if (!formData.designation?.trim()) {
      toast.error(p('toast_designation_required', { defaultValue: 'الوصف مطلوب' }));
      return;
    }

    if (editingOperationId) {
      try {
        const responsableMeta = formData.responsableId
          ? findCaisseEmployeeMeta(employeeOptions, formData.responsableId)
          : null;
        const temoinMeta = formData.temoinId
          ? findCaisseEmployeeMeta(employeeOptions, formData.temoinId)
          : null;
        const payload = mapJournalOperationToApi(
          {
            ...formData,
            responsable: responsableMeta?.name || formData.responsable,
            temoin: temoinMeta?.name || formData.temoin,
            caisse_id: defaultJournalCaisseId,
          },
          defaultJournalCaisseId
        );
        await localApiAdapter.put(`/treasury/journal/${editingOperationId}`, payload);
        notifyUpdated(showNotify, entityLabel);
        closeAddFormModal();
        await loadOperations();
        return;
      } catch (error) {
        console.error('Journal update error:', error);
        notifyError(showNotify, error.message || p('toast_save_error'));
        return;
      }
    }

    const responsableMeta = formData.responsableId
      ? findCaisseEmployeeMeta(employeeOptions, formData.responsableId)
      : null;
    const temoinMeta = formData.temoinId
      ? findCaisseEmployeeMeta(employeeOptions, formData.temoinId)
      : null;

    const skipBonWorkflow =
      (formData.typeBon === 'interne' && !formData.bonInterne?.beneficiaireId)
      || (formData.typeBon === 'externe' && !formData.bonExterne?.fichier);

    if (skipBonWorkflow) {
      try {
        const reference = formData.reference || nextCaisseEntityRef(
          settings,
          'journalOperation',
          'OPJ',
          operations,
          (op) => op.reference || op.id,
        );
        const payload = mapJournalOperationToApi(
          {
            ...formData,
            reference,
            responsable: responsableMeta?.name || formData.responsable,
            temoin: temoinMeta?.name || formData.temoin,
            caisse_id: defaultJournalCaisseId,
          },
          defaultJournalCaisseId,
        );
        await localApiAdapter.post('/treasury/journal', payload);
        notifyCreated(showNotify, entityLabel);
        closeAddFormModal();
        await loadOperations();
        return;
      } catch (error) {
        console.error('Journal create error:', error);
        notifyError(showNotify, error.message || p('toast_save_error'));
        return;
      }
    }
    
    // Validation spécifique pour les bons de commande
    if (formData.typeBon === 'interne' && !formData.bonInterne.beneficiaireId) {
      toast.error(p('toast_beneficiary_required'));
      return;
    }
    
    if (formData.typeBon === 'externe' && !formData.bonExterne.fichier) {
      toast.error(p('toast_document_required'));
      return;
    }
    
    if (formData.typeBon === 'externe' && !formData.bonExterne.fournisseurId) {
      toast.error(p('toast_supplier_required'));
      return;
    }
    
    // Générer un رمز للعملية الجديدة
    const operationReference = nextCaisseEntityRef(
      settings,
      'journalOperation',
      'OPJ',
      operations,
      (op) => op.reference || op.id,
    );
    
    // Créer une nouvelle opération
    const newOperation = {
      reference: operationReference,
      date: formData.date,
      designation: formData.designation,
      credit: formData.credit ? Number(formData.credit) : 0,
      debit: formData.debit ? Number(formData.debit) : 0,
      notes: formData.notes,
      affectation: "DEFAULT", // Default value as the field has been removed
      type: formData.credit > 0 ? 'credit' : 'debit',
      acheteur: formData.acheteur || '',
      immatriculation: formData.immatriculation || '',
      quantite: Number(formData.quantite) || 0,
      unite: formData.unite || '',
      prixUnitaire: Number(formData.prixUnitaire) || 0,
      // Ajouter les informations sur le responsable et le témoin
      responsableId: formData.responsableId || null,
      responsable: responsableMeta?.name || '',
      temoinId: formData.temoinId || null,
      temoin: temoinMeta?.name || ''
    };
    
    // Variables pour le traitement du bon
    let bonData = {};
    let bonImage = null;
    
    try {
    if (formData.typeBon === 'interne') {
      // Trouver l'entité sélectionnée (client ou fournisseur)
      const beneficiaireId = formData.bonInterne.beneficiaireId;
      let beneficiaire;
      
      // Chercher d'abord dans les clients
      beneficiaire = clients.find(client => client.id === beneficiaireId);
      
      // Si non trouvé, chercher dans les fournisseurs
      if (!beneficiaire) {
        beneficiaire = fournisseurs.find(fournisseur => fournisseur.id === beneficiaireId);
      }
      
      if (!beneficiaire) {
        toast.error(p('toast_beneficiary_not_found'));
        return;
      }
        
        // Générer un رمز للسند الداخلي
        const bonNumber = nextCaisseEntityRef(
          settings,
          'voucher',
          'BON',
          operations,
          (op) => op.bonCommande,
        );
      
      // Créer un bon interne
      bonData = {
          id: `BON-${new Date().getTime()}`,
          invoiceNumber: bonNumber,
        clientId: beneficiaireId,
        clientName: beneficiaire.name,
        clientCode: beneficiaire.code,
        total: formData.debit ? Number(formData.debit) : Number(formData.credit),
        date: formData.date,
          description: formData.designation,
          temoin: formData.temoin || '',
        type: 'internal',
        status: 'pending',
        items: [
          {
            id: 1,
              name: formData.designation || p('default_article'),
              quantity: Number(formData.quantite) || 1,
              price: formData.prixUnitaire ? Number(formData.prixUnitaire) : (formData.debit ? Number(formData.debit) : Number(formData.credit))
            }
          ]
        };

        // Préparer les données pour le rendu du template de bon
        setIsGeneratingBon(true);
        document.getElementById('bon-template').innerHTML = `
          <div id="bon-template-content" style="width: 800px; padding: 40px; background-color: white; color: black; font-family: Arial, sans-serif;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
              <div>
                <h1 style="margin: 0; font-size: 24px; color: #333;">${p('bon_title')}</h1>
                <h2 style="margin: 5px 0 0 0; font-size: 20px; color: #666;">${bonNumber}</h2>
              </div>
              <div style="text-align: right;">
                <p style="margin: 0; font-size: 14px;">${p('date')}: ${new Date(formData.date).toLocaleDateString(dateLocale)}</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${p('reference_label')}: ${operationReference}</p>
              </div>
            </div>
            
            <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
              <div>
                <h3 style="margin: 0; font-size: 16px; color: #333;">${p('bon_beneficiary')}</h3>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${beneficiaire.name}</p>
                <p style="margin: 3px 0 0 0; font-size: 14px;">${beneficiaire.code}</p>
                <p style="margin: 3px 0 0 0; font-size: 14px;">${beneficiaire.address || ''}</p>
              </div>
              <div>
                <h3 style="margin: 0; font-size: 16px; color: #333;">${p('bon_issuer')}</h3>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${p('bon_issuer_name')}</p>
                <p style="margin: 3px 0 0 0; font-size: 14px;">${p('bon_issuer_desc')}</p>
              </div>
            </div>
            
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
              <thead>
                <tr style="background-color: #f3f3f3;">
                  <th style="padding: 10px; text-align: left; border-bottom: 1px solid #ddd;">${p('bon_designation')}</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 1px solid #ddd;">${p('bon_quantity')}</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 1px solid #ddd;">${p('bon_unit')}</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 1px solid #ddd;">${p('bon_unit_price')}</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 1px solid #ddd;">${p('bon_amount')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 10px; text-align: left; border-bottom: 1px solid #ddd;">${translateJournalLabel(formData.designation)}</td>
                  <td style="padding: 10px; text-align: center; border-bottom: 1px solid #ddd;">${formData.quantite || 1}</td>
                  <td style="padding: 10px; text-align: center; border-bottom: 1px solid #ddd;">${translateUnit(formData.unite) || p('default_unit')}</td>
                  <td style="padding: 10px; text-align: right; border-bottom: 1px solid #ddd;">${formatBrandMoney(formData.prixUnitaire ? Number(formData.prixUnitaire) : (formData.debit ? Number(formData.debit) : Number(formData.credit)))}</td>
                  <td style="padding: 10px; text-align: right; border-bottom: 1px solid #ddd;">${formatBrandMoney(formData.debit ? Number(formData.debit) : Number(formData.credit))}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="4" style="padding: 10px; text-align: right; font-weight: bold;">${p('bon_total')}</td>
                  <td style="padding: 10px; text-align: right; font-weight: bold;">${formatBrandMoney(formData.debit ? Number(formData.debit) : Number(formData.credit))}</td>
                </tr>
              </tfoot>
            </table>
            
            <div style="display: flex; justify-content: space-between; margin-top: 40px;">
              <div>
                <p style="margin: 0; font-size: 14px; font-weight: bold;">${p('bon_responsible')}</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${responsableMeta?.name || p('not_specified')}</p>
                <div style="width: 150px; height: 50px; margin-top: 10px; border-bottom: 1px solid #999;"></div>
              </div>
              <div>
                <p style="margin: 0; font-size: 14px; font-weight: bold;">${p('bon_witness')}</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${temoinMeta?.name || p('not_specified')}</p>
                <div style="width: 150px; height: 50px; margin-top: 10px; border-bottom: 1px solid #999;"></div>
              </div>
              <div>
                <p style="margin: 0; font-size: 14px; font-weight: bold;">${p('bon_beneficiary_label')}</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">${beneficiaire.name}</p>
                <div style="width: 150px; height: 50px; margin-top: 10px; border-bottom: 1px solid #999;"></div>
              </div>
            </div>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              <p style="margin: 0; font-size: 12px; color: #666; text-align: center;">${p('bon_footer')}</p>
            </div>
          </div>
        `;
        
        // Attendre un moment pour que le contenu soit rendu
        setTimeout(async () => {
          try {
            const bonContent = document.getElementById('bon-template-content');
            if (bonContent) {
              const canvas = await html2canvas(bonContent);
              const bonImageUrl = canvas.toDataURL('image/png');
              
              // Ajouter l'image au bonData
              bonData.bonImage = bonImageUrl;
              
              // Nettoyer le template temporaire
              document.getElementById('bon-template').innerHTML = '';
              setIsGeneratingBon(false);
              
              // Continuer le traitement avec l'image générée
              finalizeBonCreation(newOperation, bonData);
            }
          } catch (error) {
            console.error('Failed to generate voucher image:', error);
            toast.error(p('toast_bon_generation_error'));
            setIsGeneratingBon(false);
          }
        }, 200);
        
        return; // Arrêter ici, le reste sera géré par finalizeBonCreation
      } else if (formData.typeBon === 'externe') {
        // Traitement pour bon externe (déjà implémenté)
      // Trouver le fournisseur sélectionné
      const fournisseurId = formData.bonExterne.fournisseurId;
      const fournisseur = fournisseurs.find(f => f.id === fournisseurId);
      
      if (!fournisseur) {
        toast.error(p('toast_supplier_not_found'));
        return;
      }
      
        // Créer un bon externe avec le fichier téléchargé
      const externalBonNumber = nextCaisseEntityRef(
        settings,
        'voucher',
        'BON',
        operations,
        (op) => op.bonCommande,
      );
      bonData = {
          id: `BON-${new Date().getTime()}`,
          invoiceNumber: externalBonNumber,
        clientId: fournisseurId,
        clientName: fournisseur.name,
        clientCode: fournisseur.code,
        total: formData.debit ? Number(formData.debit) : Number(formData.credit),
        montantReel: formData.bonExterne.montantReel ? Number(formData.bonExterne.montantReel) : null,
        date: formData.date,
          description: formData.designation,
        type: 'external',
        status: 'pending',
          scannedDocument: formData.bonExterne.fichier ? `bons/${operationReference}_${formData.bonExterne.fichier.name}` : 'document.pdf',
          previewUrl: formData.bonExterne.previewUrl || null,
        items: []
      };
        
        // Finaliser la création directement car nous avons déjà l'image du document
        finalizeBonCreation(newOperation, bonData);
    }
    } catch (error) {
      console.error('Failed to create voucher:', error);
      toast.error(p('toast_bon_create_error'));
    }
  };
    
  // Fonction pour finaliser la création du bon après génération de l'image
  const finalizeBonCreation = async (operation, bonData) => {
    try {
      if (bonData?.id) {
        const createdBon = addBonCommande(bonData);
        if (createdBon) {
          operation.bonCommandeId = createdBon.id;
          operation.bonCommande = createdBon.invoiceNumber;
          operation.bonCommandeType = createdBon.type;
          operation.bonImage = createdBon.bonImage || createdBon.previewUrl;
        }
      }

      const responsableMeta = operation.responsableId
        ? findCaisseEmployeeMeta(employeeOptions, operation.responsableId)
        : null;
      const temoinMeta = operation.temoinId
        ? findCaisseEmployeeMeta(employeeOptions, operation.temoinId)
        : null;

      const payload = mapJournalOperationToApi(
        {
          ...operation,
          responsable: responsableMeta?.name || operation.responsable,
          temoin: temoinMeta?.name || operation.temoin,
          caisse_id: defaultJournalCaisseId,
        },
        defaultJournalCaisseId
      );

      await localApiAdapter.post('/treasury/journal', payload);
      await loadOperations();

      notifyCreated(showNotify, entityLabel);
      setJournalFormTab('voucher');
      setShowAddForm(false);

      setFormData({
        date: new Date().toISOString().split('T')[0],
        operationType: '',
        designation: '',
        credit: '',
        debit: '',
        amount: '',
        notes: '',
        acheteur: '',
        immatriculation: '',
        quantite: '1',
        unite: 'وحدة',
        prixUnitaire: '',
        typeBon: 'interne',
        bonInterne: {
          beneficiaireId: '',
          beneficiaire: '',
          temoin: '',
          description: '',
        },
        bonExterne: {
          fichier: null,
          previewUrl: null,
          fournisseurId: '',
          fournisseur: '',
          montantReel: '',
          description: '',
        },
        responsableId: '',
        temoinId: '',
      });
    } catch (error) {
      console.error('Failed to finalize voucher:', error);
      toast.error(p('toast_save_error'));
    }
  };

  const handleTransferSubmit = (e) => {
    e.preventDefault();
    
    if (!transferData.montant) {
      toast.error(p('toast_transfer_amount_required'));
      return;
    }
    
    // Récupérer les informations sur le responsable et le témoin
    const responsableMeta = transferData.responsableId
      ? findCaisseEmployeeMeta(employeeOptions, transferData.responsableId)
      : null;
    const temoinMeta = transferData.temoinId
      ? findCaisseEmployeeMeta(employeeOptions, transferData.temoinId)
      : null;
    
    // Transférer vers la Caisse Principale via le contexte
    const result = transferJournalToPrincipale({
      date: transferData.date,
      montant: Number(transferData.montant),
      description: transferData.description,
      affectation: selectedOperations.length > 0 ? selectedOperations[0].affectation : "DIV",
      sourceId: selectedOperations.length > 0 ? selectedOperations[0].id : null,
      responsableId: transferData.responsableId || null,
      responsable: responsableMeta?.name || '',
      temoinId: transferData.temoinId || null,
      temoin: temoinMeta?.name || ''
    });
    
    if (result) {
      toast.success(p('toast_transfer_success'));
      closeTransferModal();
    } else {
      toast.error(p('toast_transfer_error'));
    }
  };

  const showTransferModalHandler = (type) => {
    setTransferType(type);
    setTransferFormTab('transfer');
    setShowTransferModal(true);
  };

  // Fonction pour ajouter une nouvelle immatriculation
  const handleAddRegistration = () => {
    if (!newRegistration.immatriculation || !newRegistration.description) {
      toast.error(p('toast_fill_all_fields'));
      return;
    }

    const exists = (vehicleRegistrations || []).some(
      reg => reg.immatriculation === newRegistration.immatriculation
    );

    if (exists) {
      toast.error(p('toast_registration_exists'));
      return;
    }

    // Ajouter l'immatriculation via le contexte
    const result = addVehicleRegistration(newRegistration);

    if (result) {
      // Reset du formulaire
      setNewRegistration({ immatriculation: '', description: '' });
      notifyCreated(showNotify, p('entity_registration_label', { defaultValue: 'التسجيل' }));
    } else {
      notifyError(showNotify, p('toast_registration_add_error'));
    }
  };

  // Fonction pour supprimer une immatriculation
  const handleDeleteRegistration = (id) => {
    const result = deleteVehicleRegistration(id);
    
    if (result) {
      notifyDeleted(showNotify, p('entity_registration_label', { defaultValue: 'التسجيل' }));
    } else {
      notifyError(showNotify, p('toast_registration_delete_error'));
    }
  };

  // Fonction pour gérer les changements dans le formulaire d'immatriculation
  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setNewRegistration(prev => ({ ...prev, [name]: value }));
  };

  // Gérer le changement de bénéficiaire/fournisseur
  const handleEntityChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'bonInterne.beneficiaireId') {
      const selectedClient = clients.find(client => client.id === value);
      if (selectedClient) {
        setFormData(prev => ({
          ...prev,
          bonInterne: {
            ...prev.bonInterne,
            beneficiaireId: value,
            beneficiaire: selectedClient.name
          }
        }));
      }
    } else if (name === 'bonExterne.fournisseurId') {
      const selectedFournisseur = fournisseurs.find(fournisseur => fournisseur.id === value);
      if (selectedFournisseur) {
        setFormData(prev => ({
          ...prev,
          bonExterne: {
            ...prev.bonExterne,
            fournisseurId: value,
            fournisseur: selectedFournisseur.name
          }
        }));
      }
    }
  };
  
  // Handle select all
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRowIds);
    }
  };

  // Handle item select
  const handleItemSelect = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  
  // Main view tabs configuration
  const mainTabs = [
    { id: 'all', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_type', nameKey: 'tab_by_type', icon: Shield },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: Layers }
  ];
  
  // Quick filter buttons configuration
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Wallet },
    { id: 'credits', nameKey: 'filter_credits', icon: DollarSign },
    { id: 'debits', nameKey: 'filter_debits', icon: CreditCard }
  ];
  
  const columnFilterRules = useMemo(() => [
    textColumnRule('operationOrder', (op) => op.reference || op.id || ''),
    textColumnRule('pieceNumber', (op) => op.pieceNumber || op.numeroPiece || ''),
    textColumnRule('date', (op) => new Date(op.date).toLocaleDateString(dateLocale)),
    textColumnRule('affectation', (op) => op.affectation || ''),
    textColumnRule('itemNumber', (op) => String(op.numeroArticle || op.itemNumber || '')),
    textColumnRule('registration', (op) => op.immatriculation || op.registration || ''),
    textColumnRule('designation', (op) => op.designation || ''),
    textColumnRule('qtyUnitPrice', (op) => `${op.quantite || ''} ${op.prixUnitaire || ''}`.trim()),
    textColumnRule('credit', (op) => (op.credit > 0 ? String(op.credit) : '')),
    textColumnRule('debit', (op) => (op.debit > 0 ? String(op.debit) : '')),
    textColumnRule('balance', (op) => String(op.solde ?? '')),
    textColumnRule('buyer', (op) => op.acheteur || op.buyer || ''),
    exactColumnRule('status', (op) => op.status || ''),
  ], [dateLocale]);

  const filteredOperations = useMemo(() => {
    const base = operationsWithSolde.filter((operation) => {
      const matchesSearch =
        operation.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (operation.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.affectation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (operation.acheteur && operation.acheteur.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType =
        filterType === 'all' ||
        (filterType === 'credit' && operation.credit > 0) ||
        (filterType === 'debit' && operation.debit > 0);

      return matchesSearch && matchesType;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [operationsWithSolde, searchTerm, filterType, columnFilters, columnFilterRules]);

  const filteredRowIds = useMemo(() => filteredOperations.map((r) => r.id), [filteredOperations]);
  const allRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  // Gérer la sélection d'un bon de commande - FUNCTION DISABLED (Modal removed)
  /* 
  const handleBonSelected = (bon) => {
    if (!selectedPieceJustificative || !bon) return;
    
    // Trouver l'opération correspondante
    const operationId = selectedPieceJustificative.replace('PJ-', '');
    const operation = operations.find(op => op.id.endsWith(operationId));
    
    if (!operation) {
      toast.error(p('toast_operation_not_found'));
      return;
    }
    
    // Lier le bon à l'opération via le contexte
    const success = linkBonCommandeToOperation(operation.id, bon.id);
    
    if (success) {
      // Mettre à jour l'état local des bons liés
      setLinkedBons(prev => ({
        ...prev,
        [selectedPieceJustificative]: bon
      }));
      
      toast.success(p('toast_bon_linked', { number: bon.invoiceNumber, piece: selectedPieceJustificative }));
    } else {
      toast.error(p('toast_bon_link_error'));
    }
  };
  */

  // Générer automatiquement un bon interne basé sur l'opération
  const handleGenerateInternalBon = (operation) => {
    if (!operation) return;
    try {
      const bonData = {
        clientName: operation.acheteur || p('default_beneficiary'),
        total: operation.debit > 0 ? operation.debit : operation.credit,
        date: operation.date,
        description: operation.designation,
        temoin: operation.temoin || '',
        type: 'internal',
        status: 'pending',
        items: [
          {
            id: 1,
            name: operation.designation || p('default_article'),
            quantity: operation.quantite || 1,
            price: operation.debit > 0 ? operation.debit : operation.credit
          }
        ]
      };

      const createdBon = addBonCommande(bonData);
      const success = linkBonCommandeToOperation(operation.id, createdBon.id);
      if (success) {
        const pjKey = `PJ-${String(operation.id).slice(-7)}`;
        setLinkedBons(prev => ({
          ...prev,
          [pjKey]: createdBon
        }));
        toast.success(p('toast_internal_bon_generated'));
      }
    } catch (error) {
      toast.error(p('toast_internal_bon_error'));
    }
  };

  // Fonction pour visualiser n'importe quel type de bon (interne ou externe)
  const handleViewExternalBon = (bon) => {
    if (!bon) {
      toast.error(p('toast_document_not_found'));
      return;
    }
    
    // Au lieu d'ouvrir dans un nouvel onglet, afficher le modal de prévisualisation
    handlePreviewImage(bon);
  };

  const handleDeleteOperation = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await localApiAdapter.delete(`/treasury/journal/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadOperations();
    } catch (error) {
      console.error('Delete journal operation error:', error);
      notifyError(showNotify, error.message || p('toast_save_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedItems.length) return;
    const count = selectedItems.length;
    if (!window.confirm(p('confirm_delete_bulk', { count, defaultValue: `Delete ${count} item(s)?` }))) return;
    try {
      await Promise.all(selectedItems.map((id) => localApiAdapter.delete(`/treasury/journal/${id}`)));
      setSelectedItems([]);
      notifyBulkDeleted(showNotify, count, entityLabelPlural);
      await loadOperations();
    } catch (error) {
      console.error('Bulk delete journal operations error:', error);
      notifyError(showNotify, error.message || p('toast_save_error'));
    }
  };

  const handleViewOperation = (operation) => {
    setViewingRecord(operation);
    setShowViewModal(true);
  };

  const handleEditOperation = (operation) => {
    setEditingOperationId(operation.id);
    setFormData({
      ...operation,
      credit: operation.credit > 0 ? operation.credit.toString() : '',
      debit: operation.debit > 0 ? operation.debit.toString() : '',
      bonInterne: operation.bonInterne || {
        beneficiaireId: '',
        beneficiaire: '',
        temoin: '',
        description: '',
      },
      bonExterne: operation.bonExterne || {
        fichier: null,
        fournisseurId: '',
        fournisseur: '',
        montantReel: '',
        description: '',
      },
    });
    setJournalFormTab('voucher');
    setShowAddForm(true);
  };

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: t('modules.caisse.pages.caisseReconciliationPage.status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'validated', label: t('modules.caisse.pages.caisseReconciliationPage.status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'cancelled', label: t('modules.caisse.pages.fundsTransferPage.status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [t]);

  const journalColumnKeys = useMemo(
    () => ['operationOrder', 'pieceNumber', 'date', 'affectation', 'itemNumber', 'registration', 'designation', 'qtyUnitPrice', 'credit', 'debit', 'balance', 'buyer', 'status', 'actions'],
    [],
  );
  const pagedOperations = useMemo(
    () => sliceCaisseListItems(filteredOperations, treasuryConfig.itemsPerPage),
    [filteredOperations, treasuryConfig.itemsPerPage],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(journalColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'operationOrder', labelKey: 'th_operation_order' },
      { key: 'pieceNumber', labelKey: 'th_piece_number' },
      { key: 'date', labelKey: 'th_date' },
      { key: 'affectation', labelKey: 'th_affectation' },
      { key: 'itemNumber', labelKey: 'th_item_number' },
      { key: 'registration', labelKey: 'th_registration' },
      { key: 'designation', labelKey: 'th_designation' },
      { key: 'qtyUnitPrice', labelKey: 'th_qty_unit_price' },
      { key: 'credit', labelKey: 'th_credit' },
      { key: 'debit', labelKey: 'th_debit' },
      { key: 'balance', labelKey: 'th_balance' },
      { key: 'buyer', labelKey: 'th_buyer' },
      { key: 'status', label: t('common.status') },
      { key: 'actions', labelKey: 'th_actions', required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    setOperations((prev) => prev.map((op) => (op.id === recordId ? { ...op, status: newStatus } : op)));
    // caisse_operations has no status column — UI-only update (avoid 404 on PATCH)
  };

  const exportRows = mapOperationsForExport(filteredOperations);
  const exportHeaders = operationExportHeaders(p);
  const exportBaseName = `journal_caisse_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = operationImportHeaders(p);
    const schema = {
      requiredFields: ['date', 'designation'],
      validators: {
        credit: (v) => !v || !Number.isNaN(Number(v)),
        debit: (v) => !v || !Number.isNaN(Number(v)),
      },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        if (!result.success) {
          toast.error(result.errors?.[0] || p('toast_save_error'));
          return;
        }
        let imported = 0;
        for (const row of result.data) {
          const credit = Number(row.credit) || 0;
          const debit = Number(row.debit) || 0;
          if (!credit && !debit) continue;
          const payload = mapJournalOperationToApi(
            {
              date: row.date,
              designation: row.designation,
              credit: credit ? String(credit) : '',
              debit: debit ? String(debit) : '',
              affectation: row.affectation || 'DEFAULT',
              caisse_id: defaultJournalCaisseId,
            },
            defaultJournalCaisseId
          );
          await localApiAdapter.post('/treasury/journal', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadOperations();
      })
      .catch((err) => {
        console.error('Import error:', err);
        toast.error(p('toast_save_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };

  const journalStatCards = useMemo(() => ([
    { titleKey: 'stat_total_operations', value: journalStats.totalOperations, icon: ClipboardList },
    { titleKey: 'stat_total_credits', value: formatBrandMoney(totals.credits), icon: DollarSign },
    { titleKey: 'stat_total_debits', value: formatBrandMoney(totals.debits), icon: CreditCard },
    { titleKey: 'stat_balance', value: formatBrandMoney(totals.solde), icon: Wallet },
  ]), [journalStats.totalOperations, totals.credits, totals.debits, totals.solde]);

  const qawaemStats = useMemo(() => sliceQawaemStats(
    journalStatCards.map((s, i) => ({
      key: s.titleKey,
      label: p(s.titleKey),
      value: s.value,
      icon: s.icon,
      variant: i % 2 ? 'forest' : 'copper',
    })),
    4,
  ), [journalStatCards, p]);

  const quickFilterLabel = p(quickFilterButtons.find((b) => b.id === quickFilter)?.nameKey || 'filter_all');

  const tabFilterOptions = useMemo(() => quickFilterButtons.map((btn) => ({
    value: btn.id,
    label: p(btn.nameKey),
  })), [quickFilterButtons, p]);

  return (
    <>
      <style>{pulseAnimation}</style>
      <QawaemErpListPageShell
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Wallet}
        stats={qawaemStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={selectedItems.length > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedItems.length})</span>
          </button>
        ) : null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
        onCreate={openAddFormModal}
        createLabel={p('add_button')}
        importExport={{
          importOpen: showImportDropdown,
          exportOpen: showExportDropdown,
          onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
          onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
          importRef: importDropdownRef,
          exportRef: exportDropdownRef,
          importLabel: p('import'),
          exportLabel: p('export'),
          generatingLabel: t('common.loading'),
          isGeneratingPDF,
          onImportCSV: () => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); },
          onImportExcel: () => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); },
          onExportCSV: handleExportCSV,
          onExportExcel: handleExportExcel,
          onExportPDF: handleExportPDF,
        }}
        headerActions={(
          <>
            <button type="button" onClick={() => loadOperations()} className="refresh-button" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className={`${
                showAdvancedOptions
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400'
                  : 'bg-white/5 border-white/20'
              } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
              title={p('advanced_options')}
            >
              <Filter className="h-5 w-5" />
              <span className="hidden sm:inline">{p('advanced_options')}</span>
              {showAdvancedOptions ? (
                <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
              ) : (
                <ChevronDown className="h-4 w-4 transition-transform" />
              )}
            </button>
            <button
              onClick={() => showTransferModalHandler('principale')}
              className="bg-white/10 text-white px-4 py-3 rounded-xl hover:bg-white/20 transition-all duration-300 flex items-center"
            >
              <RefreshCcw className="h-5 w-5 mr-2" />
              <span className="hidden sm:inline">{p('transfer')}</span>
            </button>
          </>
        )}
      >
      {/* Options Avancées - Collapsible */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Main View Tabs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {mainTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setMainViewMode(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        mainViewMode === tab.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(tab.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Filter Buttons */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {quickFilterButtons.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => {
                        setQuickFilter(btn.id);
                        if (btn.id === 'all') setFilterType('all');
                        else if (btn.id === 'credits') setFilterType('credit');
                        else if (btn.id === 'debits') setFilterType('debit');
                      }}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        quickFilter === btn.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <btn.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(btn.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Distribution Overview */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distribution_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_credits')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-green-500 to-green-600 text-white force-white">
                        {operations.filter(op => op.credit > 0).length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-green-500 to-green-600" style={{ width: `${operations.length > 0 ? (operations.filter(op => op.credit > 0).length / operations.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_operations', { percent: operations.length > 0 ? ((operations.filter(op => op.credit > 0).length / operations.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_debits')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-red-500 to-red-600 text-white force-white">
                        {operations.filter(op => op.debit > 0).length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-red-500 to-red-600" style={{ width: `${operations.length > 0 ? (operations.filter(op => op.debit > 0).length / operations.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_operations', { percent: operations.length > 0 ? ((operations.filter(op => op.debit > 0).length / operations.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_total')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white force-white">
                        {operations.length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: '100%' }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_all_operations')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Operations Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <div className="glass-table-scroll overflow-x-auto">
              <table className="w-full">
                <thead className="glass-table-head">
                  <tr>
                    <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                      <div className="flex justify-center">
                        <QawaemTableCheckbox
                          size="lg"
                          checked={allRowsSelected}
                          indeterminate={someRowsSelected}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    {isColumnVisible('operationOrder') ? (
                      <TableColumnHeaderFilter columnKey="operationOrder" label={p('th_operation_order')} value={columnFilters.operationOrder || ''} onChange={(v) => setFilter('operationOrder', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('pieceNumber') ? (
                      <TableColumnHeaderFilter columnKey="pieceNumber" label={p('th_piece_number')} value={columnFilters.pieceNumber || ''} onChange={(v) => setFilter('pieceNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('date') ? (
                      <TableColumnHeaderFilter columnKey="date" label={p('th_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('affectation') ? (
                      <TableColumnHeaderFilter columnKey="affectation" label={p('th_affectation')} value={columnFilters.affectation || ''} onChange={(v) => setFilter('affectation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('itemNumber') ? (
                      <TableColumnHeaderFilter columnKey="itemNumber" label={p('th_item_number')} value={columnFilters.itemNumber || ''} onChange={(v) => setFilter('itemNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('registration') ? (
                      <TableColumnHeaderFilter columnKey="registration" label={p('th_registration')} value={columnFilters.registration || ''} onChange={(v) => setFilter('registration', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('designation') ? (
                      <TableColumnHeaderFilter columnKey="designation" label={p('th_designation')} value={columnFilters.designation || ''} onChange={(v) => setFilter('designation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('qtyUnitPrice') ? (
                      <TableColumnHeaderFilter columnKey="qtyUnitPrice" label={p('th_qty_unit_price')} value={columnFilters.qtyUnitPrice || ''} onChange={(v) => setFilter('qtyUnitPrice', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('credit') ? (
                      <TableColumnHeaderFilter columnKey="credit" label={p('th_credit')} value={columnFilters.credit || ''} onChange={(v) => setFilter('credit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('debit') ? (
                      <TableColumnHeaderFilter columnKey="debit" label={p('th_debit')} value={columnFilters.debit || ''} onChange={(v) => setFilter('debit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('balance') ? (
                      <TableColumnHeaderFilter columnKey="balance" label={p('th_balance')} value={columnFilters.balance || ''} onChange={(v) => setFilter('balance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('buyer') ? (
                      <TableColumnHeaderFilter columnKey="buyer" label={p('th_buyer')} value={columnFilters.buyer || ''} onChange={(v) => setFilter('buyer', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColumnVisible('status') ? (
                      <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={clickableStatusOptions.map((o) => ({ value: o.value, label: o.label }))} value={columnFilters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold" />
                    ) : null}
                    <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                      label={p('th_actions')}
                      columns={qawaemColumns}
                      onToggle={handleToggleColumn}
                      visibleCount={qawaemVisibleColumnCount}
                      totalCount={qawaemColumns.length}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pagedOperations.map((operation, index) => (
                    <tr 
                      key={operation.id} 
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(operation.id)}
                            onChange={() => handleItemSelect(operation.id)}
                          />
                        </div>
                      </td>
                      {isColumnVisible('operationOrder') ? (
                        <td className="px-6 py-4 text-white font-mono text-sm">{operation.reference || operation.id}</td>
                      ) : null}
                      {isColumnVisible('pieceNumber') ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <span className="text-white">PJ-{String(operation.id).slice(-7)}</span>
                            {linkedBons[`PJ-${String(operation.id).slice(-7)}`] && (
                              <button
                                className={`p-1.5 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${
                                  linkedBons[`PJ-${String(operation.id).slice(-7)}`].type === 'internal'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-purple-500/20 text-purple-400'
                                }`}
                                onClick={() => handleViewExternalBon(linkedBons[`PJ-${String(operation.id).slice(-7)}`])}
                                title={p('view_bon_title', { number: linkedBons[`PJ-${String(operation.id).slice(-7)}`].invoiceNumber })}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {isColumnVisible('date') ? (
                        <td className="px-6 py-4 text-white/80">
                          {new Date(operation.date).toLocaleDateString(dateLocale)}
                        </td>
                      ) : null}
                      {isColumnVisible('affectation') ? (
                        <td className="px-6 py-4 text-white">{operation.affectation || '-'}</td>
                      ) : null}
                      {isColumnVisible('itemNumber') ? (
                        <td className="px-6 py-4 text-center text-white/80">{index + 1}</td>
                      ) : null}
                      {isColumnVisible('registration') ? (
                        <td className="px-6 py-4 text-white">{operation.immatriculation || '-'}</td>
                      ) : null}
                      {isColumnVisible('designation') ? (
                        <td className="px-6 py-4 text-white font-medium">{translateJournalLabel(operation.designation)}</td>
                      ) : null}
                      {isColumnVisible('qtyUnitPrice') ? (
                        <td className="px-6 py-4 text-center text-white/80">
                          {operation.quantite || 1} {translateUnit(operation.unite) || (operation.type === 'credit' ? p('default_unit') : p('default_lot'))} / {operation.prixUnitaire > 0 ? formatBrandMoney(operation.prixUnitaire, false) : '-'}
                        </td>
                      ) : null}
                      {isColumnVisible('credit') ? (
                        <td className="px-6 py-4 text-right">
                          {operation.credit > 0 ? (
                            <span className="text-green-400 font-medium">
                              {formatBrandMoney(operation.credit, false)}
                            </span>
                          ) : '-'}
                        </td>
                      ) : null}
                      {isColumnVisible('debit') ? (
                        <td className="px-6 py-4 text-right">
                          {operation.debit > 0 ? (
                            <span className="text-mahogany-400 font-medium">
                              {formatBrandMoney(operation.debit, false)}
                            </span>
                          ) : '-'}
                        </td>
                      ) : null}
                      {isColumnVisible('balance') ? (
                        <td className="px-6 py-4 text-right font-medium">
                          <span className={operation.solde >= 0 ? 'text-green-400' : 'text-mahogany-400'}>
                            {formatBrandMoney(operation.solde, false)}
                          </span>
                        </td>
                      ) : null}
                      {isColumnVisible('buyer') ? (
                        <td className="px-6 py-4 text-white/80">
                          {operation.acheteur || '-'}
                        </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={operation.status || 'pending'}
                              recordId={operation.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleViewOperation(operation)}
                          onEdit={() => handleEditOperation(operation)}
                          onDelete={() => handleDeleteOperation(operation.id)}
                          viewTitle={p('view_operation', { defaultValue: 'عرض' })}
                          editTitle={p('edit_operation')}
                          deleteTitle={p('delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      </div>

      {/* Add Form Modal — même structure / UI que Facturation > Nouveau Facturier */}
      {showAddForm && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{p('form_new_operation')}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="journal-nouvelle-operation-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {p('save')}
                </button>
                <button type="button" onClick={closeAddFormModal} className="text-white/70 hover:text-white transition-colors" aria-label={p('close')}>
                  <X size={24} />
                </button>
              </div>
            </div>
            {journalFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar tabs={journalFormTabs} activeTab={journalFormTab} onTabChange={setJournalFormTab} isLight={isLight} ariaLabel={p('form_new_operation')} />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="journal-nouvelle-operation-form" onSubmit={handleFormSubmit}>
                {(journalFormTabs.length === 0 || journalFormTab === 'voucher') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="md:col-span-3 border-b border-white/10 pb-6 mb-4">
                  
                  <div className="mb-4">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('bon_type')}</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                  <input
                          type="radio"
                          name="typeBon"
                          value="interne"
                          checked={formData.typeBon === 'interne'}
                    onChange={handleFormChange}
                          className="mr-2"
                        />
                        <span className="text-white">{p('bon_internal')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="typeBon"
                          value="externe"
                          checked={formData.typeBon === 'externe'}
                          onChange={handleFormChange}
                          className="mr-2"
                        />
                        <span className="text-white">{p('bon_external')}</span>
                      </label>
                </div>
                  </div>
                  
                  {formData.typeBon === 'interne' ? (
                    <div>
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('beneficiary')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect
                        value={formData.bonInterne.beneficiaireId}
                        onChange={(value) => handleEntityChange({ target: { name: 'bonInterne.beneficiaireId', value } })}
                        options={[
                          ...clients.filter(c => c.status === 'active').map(client => ({
                            value: `client-${client.id}`,
                            label: `${client.name} - ${client.code || p('client_label')}`
                          })),
                          ...fournisseurs.filter(f => f.status === 'active').map(fournisseur => ({
                            value: `fournisseur-${fournisseur.id}`,
                            label: `${fournisseur.name} - ${fournisseur.code || p('supplier_label')}`
                          })),
                          ...employeeOptions.map((o) => ({
                            value: `employee-${o.meta?.id}`,
                            label: `${o.meta?.name || o.label} - ${o.meta?.matricule || p('employee_label')}`
                          }))
                        ]}
                        placeholder={p('select_beneficiary')}
                        searchable={true}
                      />
                      <p className="text-xs text-white/50 mt-1">{p('beneficiary_hint')}</p>
                </div>
                  ) : (
                    <div className={CAISSE_FORM_GRID}>
                      <div className="min-w-0 md:col-span-3">
                        <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('document')} <span className="qawaem-required-mark">*</span></label>
                        <div className="flex items-center">
                  <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                    onChange={handleFormChange}
                            className="hidden"
                            id="bon-externe-file"
                          />
                          <label
                            htmlFor="bon-externe-file"
                            className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors flex items-center cursor-pointer"
                          >
                            <Upload className="h-5 w-5 mr-2" />
                            {formData.bonExterne.fichier ? p('change_document') : p('upload_document')}
                          </label>
                          {formData.bonExterne.fichier && (
                            <span className="ml-3 text-white/70">
                              {formData.bonExterne.fichier.name}
                            </span>
                          )}
                        </div>
                        
                        {/* Aperçu de l'image */}
                        {formData.bonExterne.previewUrl && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={formData.bonExterne.previewUrl} 
                                alt={p('preview_document_alt')} 
                                className="max-h-40 rounded-lg border border-white/20"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  URL.revokeObjectURL(formData.bonExterne.previewUrl);
                                  setFormData({
                                    ...formData,
                                    bonExterne: {
                                      ...formData.bonExterne,
                                      fichier: null,
                                      previewUrl: null
                                    }
                                  });
                                }}
                                className="absolute -top-2 -right-2 bg-mahogany-500 rounded-full p-0.5"
                                title={p('delete')}
                              >
                                <X className="h-4 w-4 text-white" />
                              </button>
                            </div>
                          </div>
                        )}
                </div>
                <div>
                        <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('supplier')} <span className="qawaem-required-mark">*</span></label>
                        <CustomSelect
                          value={formData.bonExterne.fournisseurId}
                          onChange={(value) => handleEntityChange({ target: { name: 'bonExterne.fournisseurId', value } })}
                          options={fournisseurs.filter(f => f.status === 'active').map(fournisseur => ({
                            value: fournisseur.id,
                            label: `${fournisseur.name} - ${fournisseur.code}`
                          }))}
                          placeholder={p('select_supplier')}
                          searchable={true}
                        />
                </div>
                    </div>
                  )}
                </div>
                  </div>
                )}
                {(journalFormTabs.length === 0 || journalFormTab === 'operation') && (
                  <div className={CAISSE_FORM_GRID}>
                    {/*
                            Grille fixe 3 colonnes (md+) — 3 lignes pleines, pas de cellule vide :
                            L1: Date | Immatriculation | Type d&apos;opération
                            L2: Type d&apos;Affectation | Désignation (×2 colonnes)
                            L3: Quantité | Unité | Prix unitaire
                          */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 md:gap-x-5 md:gap-y-6 items-start">
                              {/* Ligne 1 — 3 champs */}
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('date')}</label>
                                <CustomDatePicker
                                  value={formData.date}
                                  onChange={(newDate) => setFormData({ ...formData, date: newDate })}
                                  placeholder={p('date_placeholder')}
                                />
                              </div>
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('registration')}</label>
                                <CustomSelect
                                  value={formData.immatriculation}
                                  onChange={(value) => setFormData({ ...formData, immatriculation: value })}
                                  options={[
                                    { value: '', label: p('select_registration') },
                                    ...(vehicleRegistrations || []).map((reg) => ({
                                      value: reg.immatriculation,
                                      label: `${reg.immatriculation} - ${translateJournalLabel(reg.description)}`
                                    }))
                                  ]}
                                  placeholder={p('select_registration')}
                                  searchable={true}
                                />
                              </div>
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('affectation_code')}</label>
                                <CustomSelect
                                  value={formData.operationType}
                                  onChange={(value) => handleFormChange({ target: { name: 'operationType', value } })}
                                  options={[
                                    { value: '', label: p('operation_type') },
                                    ...operationCategories.debit.map((category) => ({
                                      value: category.category,
                                      label: translateJournalLabel(category.category)
                                    })),
                                    ...operationCategories.credit.map((category) => ({
                                      value: category.category,
                                      label: translateJournalLabel(category.category)
                                    }))
                                  ]}
                                  placeholder={p('operation_type')}
                                  searchable={true}
                                />
                              </div>

                              {/* Ligne 2 — 1 + 2 colonnes (toujours 3 colonnes de grille) */}
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('affectation_type')}</label>
                                <CustomSelect
                                  value={selectedCategory}
                                  onChange={(value) => handleFormChange({ target: { name: 'categorySelect', value } })}
                                  options={[
                                    { value: '', label: p('category') },
                                    ...categoryOptions.map((option) => ({
                                      value: option,
                                      label: translateJournalLabel(option)
                                    }))
                                  ]}
                                  placeholder={p('category')}
                                  disabled={categoryOptions.length === 0}
                                  searchable={true}
                                />
                              </div>
                              <div className="flex flex-col gap-2 min-w-0 md:col-span-2 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('affectation_designation')}</label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    name="designation"
                                    value={formData.designation}
                                    onChange={handleFormChange}
                                    onFocus={() => setShowDesignationDropdown(true)}
                                    placeholder={p('detailed_designation_placeholder')}
                                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                  />
                                  {showDesignationDropdown && designationOptions.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-slate-800 rounded-md shadow-lg max-h-60 overflow-y-auto designation-dropdown">
                                      {designationOptions.map((option, index) => (
                                        <div
                                          key={`designation-${index}`}
                                          className="px-4 py-2 text-white hover:bg-slate-700 cursor-pointer"
                                          onClick={() => {
                                            setFormData({
                                              ...formData,
                                              designation: option
                                            });
                                            setShowDesignationDropdown(false);
                                          }}
                                        >
                                          {translateJournalLabel(option)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Ligne 3 — 3 champs */}
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('quantity')}</label>
                                <input
                                  type="number"
                                  name="quantite"
                                  value={formData.quantite}
                                  onChange={handleFormChange}
                                  placeholder={p('qty_placeholder')}
                                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                              </div>
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('unit')}</label>
                                <CustomSelect
                                  value={formData.unite}
                                  onChange={(value) => setFormData({ ...formData, unite: value })}
                                  options={unitSelectOptions}
                                  placeholder={p('unit_placeholder')}
                                  searchable={true}
                                  creatable={true}
                                />
                              </div>
                              <div className="flex flex-col gap-2 min-w-0 md:min-h-[4.25rem]">
                                <label className="block w-full text-white/70 text-sm font-medium text-right">{p('unit_price')}</label>
                                <input
                                  type="number"
                                  name="prixUnitaire"
                                  value={formData.prixUnitaire}
                                  onChange={handleFormChange}
                                  placeholder={p('unit_price_placeholder')}
                                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                              </div>
                            </div>
                  </div>
                )}
                {(journalFormTabs.length === 0 || journalFormTab === 'amounts') && (
                  <div className={CAISSE_FORM_GRID}>
                    {/* Montant unique */}
                <div className="md:col-span-2">
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('amount')}</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Effacer les anciennes valeurs
                      const updatedForm = {
                        ...formData,
                        amount: value,
                        credit: '',
                        debit: ''
                      };
                      
                      // Si on a déjà sélectionné un type d'opération, mettre le montant au bon endroit
                      if (selectedOperationType) {
                        if (debitTypes.includes(selectedOperationType)) {
                          updatedForm.debit = value;
                        } else if (creditTypes.includes(selectedOperationType)) {
                          updatedForm.credit = value;
                        }
                      }
                      
                      setFormData(updatedForm);
                    }}
                    placeholder={p('amount_placeholder')}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                  <p className="text-xs text-white/50 mt-1">{p('amount_hint')}</p>
                </div>

                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('payment_method')}</label>
                  <CustomSelect
                    value={formData.paymentMethod || 'cash'}
                    onChange={(value) => setFormData({ ...formData, paymentMethod: value })}
                    options={paymentMethodOptions.map((m) => ({ value: m.value, label: m.label }))}
                    placeholder={p('payment_method')}
                  />
                </div>
                
                {/* Acheteur - désormais liste déroulante des employés */}
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('buyer')}</label>
                  <CustomSelect
                    value={formData.acheteur}
                    onChange={(value) => setFormData({ ...formData, acheteur: value })}
                    options={buyerSelectOptions}
                    placeholder={p('select_buyer')}
                    searchable={true}
                  />
                  <p className="text-xs text-white/50 mt-1">{p('buyer_hint')}</p>
                </div>
                
                {/* Responsable - champ retiré selon nouvelle spécification */}
                {false && (
                      <div>
                  <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('responsible')}</label>
                        <select
                          name="responsableId"
                          value={formData.responsableId}
                          onChange={handleFormChange}
                          className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-mahogany-500"
                        >
                          <option value="">{p('select_responsible')}</option>
                    {employees.filter(emp => emp.role === 'manager' || emp.role === 'buyer' || emp.role === 'cashier').map(employee => (
                            <option key={employee.id} value={employee.id}>
                              {employee.firstName} {employee.lastName} - {employee.position}
                            </option>
                          ))}
                        </select>
                  <p className="text-xs text-white/50 mt-1">{p('transfer_responsible_hint')}</p>
                      </div>
                )}
                
                {/* Témoin supprimé selon nouvelle spécification */}
                {false && (
                  <div></div>
                )}
                
                {/* Notes */}
                      <div className="md:col-span-3">
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('notes')}</label>
                        <textarea
                    name="notes"
                    value={formData.notes}
                          onChange={handleFormChange}
                    placeholder={p('notes_placeholder')}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all min-h-[100px]"
                        ></textarea>
                </div>
                      <div className="min-w-0 md:col-span-3">
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                          <p className="text-blue-300 text-sm">{p('documents_hint')}</p>
                        </div>
                      </div>
                  </div>
                )}
                {journalFormTabs.length > 3 && journalFormTab === 'documents' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0 md:col-span-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <p className="text-blue-300 text-sm">
                              {p('documents_hint')}
                            </p>
                          </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Transfer Modal */}
      {showTransferModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={closeTransferModal} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${cfgTheme.title}`}>
                {p('transfer_modal_title', { target: transferType === 'principale' ? p('transfer_target_main') : p('transfer_target_monthly') })}
              </h2>
            </div>
            {transferFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar tabs={transferFormTabs} activeTab={transferFormTab} onTabChange={setTransferFormTab} isLight={isLight} ariaLabel={p('transfer_modal_title', { target: transferType === 'principale' ? p('transfer_target_main') : p('transfer_target_monthly') })} />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
            <form id="journal-transfer-form" onSubmit={handleTransferSubmit}>
              {(transferFormTabs.length === 0 || transferFormTab === 'transfer') && (
                <div className={CAISSE_FORM_GRID}>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('date')}</label>
                    <input
                      type="date"
                      name="date"
                      value={transferData.date}
                      onChange={handleTransferChange}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-mahogany-500"
                      required
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('transfer_total_amount')}</label>
                    <input
                      type="number"
                      name="montant"
                      value={transferData.montant}
                      onChange={handleTransferChange}
                      placeholder={p('transfer_amount_placeholder')}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-mahogany-500"
                      required
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('transfer_description')}</label>
                    <input
                      type="text"
                      name="description"
                      value={transferData.description}
                      onChange={handleTransferChange}
                      placeholder={p('transfer_description_placeholder')}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-mahogany-500"
                    />
                  </div>
                  {transferFormTabs.length === 0 ? (
                    <>
                      <div className="min-w-0">
                        <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('responsible')}</label>
                        <CustomSelect
                          value={transferData.responsableId}
                          onChange={(value) => setTransferData({ ...transferData, responsableId: value })}
                          options={responsibleSelectOptions}
                          placeholder={p('select_responsible')}
                          searchable={true}
                        />
                        <p className="text-xs text-white/50 mt-1">{p('transfer_responsible_hint')}</p>
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('witness')}</label>
                        <CustomSelect
                          value={transferData.temoinId}
                          onChange={(value) => setTransferData({ ...transferData, temoinId: value })}
                          options={witnessSelectOptions}
                          placeholder={p('select_witness')}
                          searchable={true}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              )}
              {transferFormTabs.length > 0 && transferFormTab === 'responsible' && (
                <div className={CAISSE_FORM_GRID}>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('responsible')}</label>
                    <CustomSelect
                      value={transferData.responsableId}
                      onChange={(value) => setTransferData({ ...transferData, responsableId: value })}
                      options={responsibleSelectOptions}
                      placeholder={p('select_responsible')}
                      searchable={true}
                    />
                    <p className="text-xs text-white/50 mt-1">{p('transfer_responsible_hint')}</p>
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('witness')}</label>
                    <CustomSelect
                      value={transferData.temoinId}
                      onChange={(value) => setTransferData({ ...transferData, temoinId: value })}
                      options={witnessSelectOptions}
                      placeholder={p('select_witness')}
                      searchable={true}
                    />
                    <p className="text-xs text-white/50 mt-1">{p('transfer_witness_hint')}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={closeTransferModal}
                  className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
                >
                  {p('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-mahogany-600 to-amber-600 text-white rounded-xl hover:from-mahogany-700 hover:to-amber-700 transition-all"
                >
                  {p('transfer_submit')}
                </button>
              </div>
            </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Bon Modal - REMOVED (Modal component deleted) */}
      {/*
      <BonCommandeModal 
        isOpen={showBonCommandeModal}
        onClose={() => setShowBonCommandeModal(false)}
        pieceJustificative={selectedPieceJustificative}
        onBonSelected={handleBonSelected}
      />
      */}

      {/* Input file caché pour le téléchargement depuis le tableau */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".jpg,.jpeg,.png"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Modal de prévisualisation d'image */}
      {showImagePreview && previewImage && ReactDOM.createPortal(
        <div
          className={QAWAEM_MODAL_OVERLAY_CLASS}
          onClick={() => {
            setShowImagePreview(false);
            setPreviewImage(null);
          }}
          role="presentation"
        >
          <div
            className={`relative max-w-4xl max-h-[90vh] overflow-auto p-4 rounded-2xl border ${isLight ? 'bg-white border-slate-200' : 'bg-white/5 border-white/20 backdrop-blur-md'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton de fermeture en haut à droite */}
            <button
              onClick={() => {
                setShowImagePreview(false);
                setPreviewImage(null);
              }}
              className="absolute top-4 right-4 bg-red-500/70 hover:bg-red-500 p-2 rounded-full transition-colors z-10"
              aria-label={p('close')}
            >
              <X className="h-6 w-6 text-white" />
            </button>
            
            <img 
              src={previewImage} 
              alt={p('preview_alt')} 
              className="max-w-full rounded-lg"
            />
            
            {/* Bouton de fermeture en bas centré */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => {
                  setShowImagePreview(false);
                  setPreviewImage(null);
                }}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-xl transition-colors flex items-center"
              >
                <X className="h-5 w-5 mr-2" />
                {p('preview_close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={p('view_operation', { defaultValue: 'عرض العملية' })}
          icon={Eye}
          maxWidth="800px"
        >
              <div className={CAISSE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_operation_order')}</p>
                  <p className={`font-medium font-mono ${cfgTheme.title}`}>{viewingRecord.reference || viewingRecord.id}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_date')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{new Date(viewingRecord.date).toLocaleDateString(dateLocale)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_designation')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{translateJournalLabel(viewingRecord.designation)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_affectation')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.affectation || '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_credit')}</p>
                  <p className="text-green-500 font-bold">{viewingRecord.credit > 0 ? formatBrandMoney(viewingRecord.credit) : '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_debit')}</p>
                  <p className="text-red-500 font-bold">{viewingRecord.debit > 0 ? formatBrandMoney(viewingRecord.debit) : '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_balance')}</p>
                  <p className={`font-bold ${viewingRecord.solde >= 0 ? 'text-green-500' : 'text-red-500'}`}>{formatBrandMoney(viewingRecord.solde ?? 0)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_buyer')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.acheteur || '-'}</p>
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {/* Élément caché pour la génération du bon */}
      <div id="bon-template" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}></div>
      
      {/* Indicateur de chargement pendant la génération */}
      {isGeneratingBon && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} role="presentation">
          <div className={`rounded-3xl p-6 flex flex-col items-center border ${isLight ? 'bg-white border-slate-200 shadow-lg' : 'bg-white/10 backdrop-blur-lg border-white/20'}`}>
            <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mb-4 ${isLight ? 'border-blue-600' : 'border-white'}`} />
            <p className={cfgTheme.title}>{p('generating_bon')}</p>
          </div>
        </div>,
        document.body
      )}
      </QawaemErpListPageShell>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default CaisseJournalPage; 


